/**
 * siglasEducativas.ts — expresión única de las siglas educativas punteadas
 *
 * Origen: `SIGRE` 12-ago-2026, `DOP-SIGRE-FORMA`=(b) confirmada por Luis.
 *
 * Por qué existe este fichero
 * ---------------------------
 * El repo llegó a tener el mismo concepto expresado de cuatro maneras distintas
 * (`InfrastructureClassifier`, `TypologyVoter`, `documentExtractor`,
 * `TableDetector`, más dos normalizadores en `addressCleaner` y `FuzzySearch`).
 * Tres de ellas no contemplaban siquiera la forma punteada. Esta constante es
 * el único sitio donde esa forma se escribe.
 *
 * Dos lecciones incorporadas, ambas medidas sobre el corpus real
 * --------------------------------------------------------------
 * 1. `\b` NO puede usarse para anclar una sigla punteada: no se satisface tras
 *    un punto literal, porque ni «.» ni el espacio siguiente son `\w`. Es el
 *    defecto que `SIGLA` (11-ago) corrigió en `InfrastructureClassifier`. Se
 *    ancla con *lookarounds* propios; el punto de cierre es opcional para
 *    admitir «C.E.I.P» sin él (6 de 73 ocurrencias del corpus).
 * 2. El 15 % de las ocurrencias en alcance llevan un espacio intercalado
 *    («C. E.I.P.» ×8, «I. E.S.» ×3 sobre 73). Se admite **un solo espacio**
 *    —no `\s*`— para no cruzar saltos de línea ni fronteras de celda.
 *
 * Lo que deliberadamente NO cubre
 * -------------------------------
 * - `C.E.I.S.` (24 ocurrencias, la 2.ª forma más frecuente del corpus): en
 *   contexto PTEL es servicio de extinción de incendios, **no** un centro
 *   educativo. Está fijada como caso de no-invasión en
 *   `InfrastructureClassifier.sigla.test.ts` y aquí también.
 * - Formas huérfanas `C.E.I.`, `E.I.` — frente (3), requieren criterio
 *   semántico, no de expresión regular.
 * - `FUEL` / `e.s.` — frente (2), dos letras dan un radio de falsos positivos
 *   de otro orden. Coordinar con `OBS-GEO-ADAPTADOR-CUATRO-TIPOS-A-OTRO`.
 *
 * Medición del corpus (47 ODT/ODS reales, `SIGRE` 12-ago):
 * 117 ocurrencias punteadas frente a 21 planas — la forma que el código
 * reconocía era la minoritaria por 5,6 a 1.
 */

import { normalizeForSearch } from './fuzzySearch';

/**
 * Casa las siglas educativas escritas con puntos, con o sin punto de cierre y
 * con un espacio opcional tras cada punto.
 *
 * Sin bandera `g`: es un patrón compartido y `lastIndex` haría los `test()`
 * dependientes del orden de llamada.
 *
 * @example
 * SIGLA_EDUCATIVA_PUNTEADA.test('C.E.I.P. San José')  // true
 * SIGLA_EDUCATIVA_PUNTEADA.test('C. E.I.P. Sixto')    // true
 * SIGLA_EDUCATIVA_PUNTEADA.test('I.E.S. Sierra Mágina') // true
 * SIGLA_EDUCATIVA_PUNTEADA.test('C.E.I.S. Provincial') // false — no es educativo
 */
export const SIGLA_EDUCATIVA_PUNTEADA =
  /(?<![\wáéíóúñ.])(?:c\.[ ]?e\.[ ]?i\.[ ]?p|i\.[ ]?e\.[ ]?s)\.?(?![\wáéíóúñ])/i;

/**
 * ¿El texto contiene una sigla educativa en forma punteada?
 *
 * Envoltorio explícito para que las rutas consumidoras no dependan de la
 * bandera ni del `lastIndex` del patrón compartido.
 */
export function tieneSiglaEducativaPunteada(texto: string): boolean {
  return !!texto && SIGLA_EDUCATIVA_PUNTEADA.test(texto);
}

// ============================================================================
// PREFIJO DE TIPO DE CENTRO · `PREFEDU` 29-oct-2026, `DOP-PREFEDU-1`=(a)
// ============================================================================

/**
 * Formas largas del tipo de centro, de la más larga a la más corta (la
 * alternancia toma la primera que casa). Medidas en la copia de DERA del
 * 29-oct-2026, que cambió «C.E.I.P. X» por «Colegio de Educación Infantil y
 * Primaria X» (1 402 centros), «I.E.S. X» por «Instituto de Educación
 * Secundaria X» (670), «E.I. X» por «Escuela Infantil X» y «C.D.P. X» por «X».
 *
 * `PREFEDU-b3` DOP-1 (a): sinónimos del tipo que escriben los planes y DERA no usa nunca (0 nombres en la copia
 * del 3-nov-2026): «Guardería (Municipal)» = Escuela Infantil, «Escuela Hogar», «Escuela de Artes Aplicadas (y
 * Oficios Artísticos)» = Escuela de Arte, «Centro (Público) de Educación de Adultos» y «Escuela de Adultos» =
 * Educación Permanente. Benahadux, Colomera, Guadix y La Guardia de Jaén. Evidencia: `.ptel/p0_prefedub3_20261103/`.
 */
const FORMA_LARGA =
  '(?:guarder[ií]a(?:\\s+municipal)?' +
  '|escuela\\s+hogar' +
  '|escuela\\s+de\\s+artes\\s+aplicadas(?:\\s+y\\s+oficios\\s+art[ií]sticos)?' +
  '|escuela\\s+de\\s+arte' +
  '|centro\\s+(?:p[uú]blico\\s+)?de\\s+educaci[oó]n\\s+de\\s+adultos' +
  '|escuela\\s+de\\s+adultos' +
  '|colegio\\s+de\\s+educaci[oó]n\\s+(?:infantil\\s+y\\s+primaria|infantil|primaria)' +
  '|colegio\\s+p[uú]blico(?:\\s+rural)?' +
  '|instituto\\s+de\\s+educaci[oó]n\\s+secundaria(?:\\s+obligatoria)?' +
  '|centro\\s+(?:docente\\s+)?privado(?:\\s+concertado)?(?:\\s+de\\s+educaci[oó]n\\s+(?:infantil|primaria|secundaria|especial))?' +
  '|centro\\s+(?:espec[ií]fico\\s+)?de\\s+educaci[oó]n\\s+(?:infantil|permanente|especial)' +
  '|secci[oó]n\\s+de\\s+educaci[oó]n\\s+permanente' +
  '|escuela\\s+(?:de\\s+educaci[oó]n\\s+)?infantil' +
  '|escuela\\s+(?:autorizada\\s+|municipal\\s+)?de\\s+m[uú]sica' +
  '|colegio|instituto)';

/** Siglas sin puntos («CEIP», «IES»…). Van seguidas de espacio, punto o fin. */
const SIGLA_COMPACTA = '(?:ceip|cepr|ceper|cpr|cdp|cei|cee|ieso|ies|cp|ei)';

/**
 * Siglas con puntos («C.E.I.P.», «C. E.I.P», «C.E.PR.»): cada letra seguida de
 * punto y, como mucho, un espacio; el punto final es opcional. Mismo criterio
 * de un solo espacio que `SIGLA_EDUCATIVA_PUNTEADA`.
 */
const SIGLA_PUNTEADA =
  '(?:c\\.\\s?e\\.\\s?i\\.\\s?p|c\\.\\s?e\\.\\s?p\\.\\s?e\\.\\s?r|c\\.\\s?e\\.\\s?pr|c\\.\\s?p\\.\\s?r' +
  '|c\\.\\s?d\\.\\s?p|c\\.\\s?e\\.\\s?i|c\\.\\s?e\\.\\s?e|i\\.\\s?e\\.\\s?s\\.\\s?o|i\\.\\s?e\\.\\s?s|c\\.\\s?p|e\\.\\s?i|e\\.\\s?a|c\\.\\s?c)\\.?' +
  // que no siga otra letra con punto: «C.E.I.S.» no es «C.E.I.» + «S.»
  '(?!\\.?\\s?[a-z]\\.)';

const PREFIJO_EDUCATIVO = new RegExp(
  `^\\s*(?:${FORMA_LARGA}(?=[\\s.,:;-]|$)|${SIGLA_PUNTEADA}|${SIGLA_COMPACTA}(?=[\\s.,:;-]|$))[\\s.,:;-]*`,
  'i',
);

/**
 * Quita el prefijo de tipo de centro educativo del principio de un nombre:
 * «C.E.I.P. El Pinar» → «El Pinar»; «Colegio de Educación Infantil y Primaria
 * Real Mentesa» → «Real Mentesa»; «C.E.I.P.PADRE Manjón» → «PADRE Manjón».
 *
 * Si no queda nada (la fila dice solo «Colegio»), devuelve el nombre tal cual:
 * quitarlo dejaría una búsqueda vacía.
 *
 * **Solo para emparejar dentro de un conjunto ya educativo** (la búsqueda local
 * de DERA en la categoría `education`). No sirve para clasificar: incluye
 * «C.E.I.» y la forma compacta «CP», que fuera de ese conjunto chocan con
 * «C.E.I.S.» (bomberos, excluida arriba) y con otras siglas.
 */
export function quitarPrefijoEducativo(nombre: string): string {
  if (!nombre) return nombre;
  // `PREFEDU-b1` DOP-3: hasta dos prefijos seguidos («C.D.P. Colegio Maristas…» → «Maristas…»),
  // cada uno solo si deja algo detrás.
  let actual = nombre;
  for (let i = 0; i < 2; i++) {
    const resto = actual.replace(PREFIJO_EDUCATIVO, '').trim();
    if (resto.length === 0 || resto === actual) break;
    actual = resto;
  }
  return actual;
}

/**
 * ¿El nombre no es más que el prefijo («E.I.», «Colegio», «Instituto»)? Entonces no
 * identifica un centro, salvo que en el municipio solo haya uno de ese tipo.
 */
export function esSoloPrefijoEducativo(nombre: string): boolean {
  return !!nombre && nombre.replace(PREFIJO_EDUCATIVO, '').trim().length === 0 && PREFIJO_EDUCATIVO.test(nombre);
}

// ============================================================================
// GUARDA DEL REINTENTO SIN PREFIJO · `PREFEDU` 29-oct-2026, `DOP-PREFEDU-3`
// ============================================================================
//
// Quitar el prefijo deja nombres cortos que casan con otro tipo de centro
// («I.E.S. Triana» → «Conservatorio Elemental de Música Triana») o con la
// dirección de otro centro («Medina Olmos»). Examen fuera de muestra sobre los
// 3 832 centros renombrados por la Junta (ajuste en Almería, Cádiz, Córdoba y
// Granada; examen en Huelva, Jaén, Málaga y Sevilla): 763 recuperados en la
// mitad no vista con 0 errores. Evidencia: `.ptel/p0_prefedu_20261029/`.

/** Tipo de centro educativo leído del principio del nombre, o `null` si no lo dice. */
export type TipoCentroEducativo =
  | 'primaria' | 'secundaria' | 'infantil' | 'especial' | 'permanente'
  | 'musica' | 'arte' | 'convenio' | 'privado' | 'hogar';

export function tipoCentroEducativo(nombre: string): TipoCentroEducativo | null {
  const s = normalizeForSearch(nombre.replace(/\./g, ''));
  if (/^(instituto|iesos?|ies)\b/.test(s)) return 'secundaria';
  if (/^(escuela( de educacion)? infantil|centro (docente )?privado( concertado)? de educacion infantil|centro de educacion infantil|cei|ei|guarderia)\b/.test(s)) return 'infantil';
  if (/^(centro (docente )?privado( concertado)? de educacion especial|centro (especifico )?de educacion especial|cee)\b/.test(s)) return 'especial';
  if (/^(seccion de educacion permanente|centro de educacion permanente|ceper|sep|centro (publico )?de educacion de adultos|escuela de adultos)\b/.test(s)) return 'permanente';
  if (/^(conservatorio|escuela (municipal |autorizada )?de musica)\b/.test(s)) return 'musica';
  if (/^(escuela de arte|escuela de artes aplicadas|ea)\b/.test(s)) return 'arte';
  // `PREFEDU-b3`: tipo propio de DERA (23 centros); sin él, «Escuela Hogar Divina Infantita» (Guadix 54) casaría con el
  // centro docente privado «Divina Infantita», que es otro centro.
  if (/^escuela hogar\b/.test(s)) return 'hogar';
  if (/^(centro de convenio|cc)\b/.test(s)) return 'convenio';
  if (/^(centro (docente )?privado|cdp)\b/.test(s)) return 'privado';
  if (/^(colegio|ceip|cepr|cpr|cp)\b/.test(s)) return 'primaria';
  return null;
}

/** Abreviaturas frecuentes en los nombres de DERA («Ntra. Sra. del Carmen»): se comparan desplegadas. */
const ABREVIATURAS: Readonly<Record<string, string>> = {
  ntra: 'nuestra', ntro: 'nuestro', sra: 'senora', sr: 'senor', sto: 'santo', sta: 'santa',
};
/**
 * `PREFEDU-b1` DOP-2: palabras que no cuentan al comparar, en los dos nombres («CEIP Adelantado
 * Pedro de Mendoza» frente a «… Adelantado Pedro Mendoza», Guadix). Solo «de» y «del»: con los
 * artículos («la», «los», «el»…) el examen de los 3 832 renombrados dio un error nuevo («E.I. Los
 * Colores» ⇒ «… Burbujas de Colores II») y cuatro aciertos perdidos.
 */
const PALABRAS_VACIAS: ReadonlySet<string> = new Set(['de', 'del']);
const palabrasDe = (n: string) =>
  normalizeForSearch(n).split(' ')
    .filter((w) => w.length > 1 && !PALABRAS_VACIAS.has(w))
    .map((w) => ABREVIATURAS[w] ?? w);

/**
 * `PREFEDU-b1` DOP-1: el nivel pegado al nombre del plan («C.E.I.P. Las Encinas Primaria», Alfacar)
 * vale si el centro de DERA lo lleva en su nombre completo, tipo incluido («Colegio de Educación
 * Infantil y Primaria Las Encinas»).
 */
const PALABRAS_DE_NIVEL: ReadonlySet<string> = new Set(['infantil', 'primaria', 'secundaria']);

/** ¿Los dos nombres dicen un tipo de centro y es distinto? («E.I. El Bosque» frente a «Sección de Educación Permanente El Bosque»). */
export function tipoContradice(nombrePtel: string, nombreCandidato: string): boolean {
  const a = tipoCentroEducativo(nombrePtel);
  const b = tipoCentroEducativo(nombreCandidato);
  if (!a || !b || a === b) return false;
  // «Privado» dice quién lo gestiona, no el nivel: un centro privado de educación especial o
  // infantil no contradice a otro que también se declara privado (Guadix, fila 263).
  const PRIVADO = /privad|\bc\.?\s?d\.?\s?p\b/i;
  // `PREFEDU-b1` DOP-3: un centro privado puede llamarse «Colegio …» («C.D.P. Colegio Maristas…»
  // frente a «Colegio Maristas…»): «privado» no contradice a «colegio».
  // Solo «Colegio» a secas (no «C.E.I.P.», «Colegio Público» ni «Colegio de Educación…», que sí dicen nivel y titularidad).
  const colegioASecas = (n: string) => /^colegio (?!publico|de educacion)/.test(normalizeForSearch(n.replace(/\./g, '')));
  if ((a === 'privado' && b === 'primaria' && colegioASecas(nombreCandidato))
    || (a === 'primaria' && b === 'privado' && colegioASecas(nombrePtel))) return false;
  return !(PRIVADO.test(nombrePtel) && PRIVADO.test(nombreCandidato));
}

/**
 * Elige, entre los candidatos de una búsqueda hecha con el nombre sin prefijo,
 * el centro que corresponde a `nombrePtel`, o `null` si no hay uno seguro:
 *  1. todas las palabras del nombre (sin prefijo) están en el **nombre** del
 *     candidato (sin prefijo) — la dirección no cuenta;
 *  2. el tipo no se contradice (si los dos lo dicen, ha de ser el mismo);
 *  3. se prefieren los del mismo tipo; si quedan centros en puntos distintos,
 *     es un empate y no se elige ninguno (mejor sin coordenada que con otra).
 */
export function elegirCentroEducativo<T extends { nombre: string; x: number; y: number }>(
  nombrePtel: string,
  candidatos: readonly T[],
): T | null {
  const buscadas = palabrasDe(quitarPrefijoEducativo(nombrePtel));
  if (buscadas.length === 0) return null;
  const tipoPtel = tipoCentroEducativo(nombrePtel);
  const validos = candidatos.filter((c) => {
    const propias = new Set(palabrasDe(quitarPrefijoEducativo(c.nombre)));
    const completas = new Set(palabrasDe(c.nombre));
    const esta = (p: string) => propias.has(p) || (PALABRAS_DE_NIVEL.has(p) && completas.has(p));
    if (!buscadas.every(esta)) return false;
    return !tipoContradice(nombrePtel, c.nombre);
  });
  const mismoTipo = validos.filter((c) => tipoPtel && tipoCentroEducativo(c.nombre) === tipoPtel);
  const pool = mismoTipo.length > 0 ? mismoTipo : validos;
  const puntos = new Set(pool.map((c) => `${Math.round(c.x)},${Math.round(c.y)}`));
  return pool.length > 0 && puntos.size === 1 ? pool[0] : null;
}

/**
 * `PREFEDU-b3` DOP-2 (a) · un nombre que solo dice el tipo («Escuela Municipal de Música», «E.I.», «Guardería»)
 * vale si en el municipio hay un único centro de ese tipo (o todos en un mismo punto), no un único centro
 * cualquiera: «Guardería» no es el único colegio del pueblo (Benalúa de las Villas). Si el tipo grueso tiene
 * varios («Escuela Municipal de Música» junto a un conservatorio), se afina con el tipo de DERA escrito igual
 * que el nombre del plan. `centros[].nombre` ha de llevar el tipo (`nombreConTipoDera`). Examen de los 3 832
 * renombrados: TSF +13, `searchLocal` +5, 0 errores nuevos. Lo usan `searchLocal` y TSF.
 */
export function elegirUnicoDelTipo<T extends { nombre: string; tipoDera?: string; x: number; y: number }>(
  nombrePtel: string,
  centros: readonly T[],
): T | null {
  if (!esSoloPrefijoEducativo(nombrePtel)) return null;
  const tipo = tipoCentroEducativo(nombrePtel);
  if (!tipo) return null;
  const unPunto = (xs: readonly T[]) =>
    xs.length > 0 && new Set(xs.map((c) => `${Math.round(c.x)},${Math.round(c.y)}`)).size === 1 ? xs[0] : null;
  const delTipo = centros.filter((c) => tipoCentroEducativo(c.nombre) === tipo);
  const grueso = unPunto(delTipo);
  if (grueso) return grueso;
  const n = normalizeForSearch(nombrePtel.replace(/\./g, ''));
  return unPunto(delTipo.filter((c) => !!c.tipoDera && normalizeForSearch(c.tipoDera) === n));
}
